import React from 'react';
import { useTranslation } from 'react-i18next';
import { Repeat, Clock, MoreVertical, Trash2, ChevronRight, ChevronDown, Eye, EyeOff, Check } from 'lucide-react';
import { SELECTOR_STAGES, getStageClass, getStageColor, getRelativeDateDisplay, formatTiempoDisplay } from '../lib/taskGrid';

// slice 02.1 · paso b.4 — la vista de LISTA (`minimal-task-table`) de la pantalla de tareas.
// Presentacional: recibe las filas ya paginadas (`tasks`, con divisores) + `on*` + los slots de
// popover (`renderCalendlyPopover`/`renderDurationPopover`/`renderUserCell`/`renderUserSelectPopover`,
// que el contenedor arma con las hojas de b.3) + los ids de "qué popover está abierto".
export const TaskListView = ({
    tasks,
    userTz,
    columnWidths,
    resizingCol,
    currentPage,
    totalPages,
    collapsedStages = {},
    onToggleStage,
    showCompletedTasks = true,
    onToggleShowCompleted,
    getTaskPermissions,
    activeCalendlyPopoverTaskId,
    activeDurationPopoverTaskId,
    activeUserSelectTaskId,
    activeUserSelectField,
    activeTaskDropdownId,
    deletingTaskId,
    onColResizeStart,
    onColResizeMove,
    onColResizeEnd,
    onRowDragStart,
    onCheckboxChange,
    onStageChange,
    onTitleClick,
    onPriorityChange,
    onOpenCalendly,
    onOpenDuration,
    onToggleDropdown,
    onStartDelete,
    onCancelDelete,
    onDeleteTask,
    onPageChange,
    renderCalendlyPopover,
    renderDurationPopover,
    renderUserCell,
    renderUserSelectPopover,
}) => {
    const { t: translate } = useTranslation('tasks');

    const resizeHandle = (col) => (
        <div
            className={`th-resize-handle ${resizingCol === col ? 'resizing' : ''}`}
            onPointerDown={(e) => onColResizeStart(e, col)}
            onPointerMove={onColResizeMove}
            onPointerUp={(e) => onColResizeEnd(e, e.pointerId)}
        />
    );

    return (
        <div className="list-tab-panel">

            <div className="table-responsive">
                <table className="minimal-task-table" style={{ tableLayout: 'fixed' }}>
                    <thead>
                        <tr>
                            <th style={{ width: '30px' }}></th>
                            <th style={{ width: `${columnWidths.stage}px` }}>{translate('table.headers.stage')}{resizeHandle('stage')}</th>
                            <th style={{ width: `${columnWidths.title}px` }}>{translate('table.headers.title')}{resizeHandle('title')}</th>
                            <th style={{ width: `${columnWidths.date}px` }}>{translate('table.headers.date')}{resizeHandle('date')}</th>
                            <th style={{ width: `${columnWidths.tiempo}px` }}>{translate('table.headers.time')}{resizeHandle('tiempo')}</th>
                            <th style={{ width: `${columnWidths.priority}px` }}>{translate('table.headers.priority')}{resizeHandle('priority')}</th>
                            <th style={{ width: `${columnWidths.assignee}px` }}>{translate('table.headers.assignee')}{resizeHandle('assignee')}</th>
                            <th style={{ width: `${columnWidths.supervisor}px` }}>{translate('table.headers.supervisor')}{resizeHandle('supervisor')}</th>
                            <th style={{ width: '50px' }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {tasks.map(task => {
                            if (task.isStageDivider) {
                                const collapsed = !!collapsedStages[task.stage];
                                const color = getStageColor(task.stage);
                                return (
                                    <tr key={task.id} className="task-stage-divider-row">
                                        <td colSpan="9" style={{ borderBottom: 'none', padding: 0 }}>
                                            <button
                                                type="button"
                                                className="task-stage-divider"
                                                onClick={() => onToggleStage?.(task.stage)}
                                            >
                                                {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                                                <span className="task-stage-dot" style={{ backgroundColor: color }} />
                                                <span className="task-stage-label">{translate(`stages.${task.stage}`, { defaultValue: task.stage })}</span>
                                                <span className="task-stage-count">{task.count}</span>
                                            </button>
                                        </td>
                                    </tr>
                                );
                            }
                            if (task.isDivider) {
                                return (
                                    <tr key="unassigned_divider_row">
                                        <td colSpan="9" style={{ padding: 'var(--space-8) 0 var(--space-4) 0', borderBottom: 'none' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', color: 'rgba(255,255,255,0.5)', fontSize: '12px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                <span style={{ marginRight: 'var(--space-4)', fontSize: '14px' }}>⚠️ Tareas sin encargado</span>
                                                <div style={{ flex: 1, height: '1px', backgroundColor: 'rgba(255,255,255,0.1)' }}></div>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            }
                            return (
                                <tr
                                    key={task.id + (task.isSubtask ? '_sub' : '')}
                                    className={task.checked ? 'completed-row' : ''}
                                    draggable={!task.isSubtask}
                                    onDragStart={(e) => !task.isSubtask && onRowDragStart(e, task.id)}
                                >
                                    <td>
                                        {/* Donut checkbox coloreado por etapa — integración main→dev epic C2 (`1992b9d8`). */}
                                        <button
                                            type="button"
                                            className={`task-donut-checkbox ${task.checked ? 'checked' : ''}`}
                                            style={{ '--donut-color': getStageColor(task.stage) }}
                                            onClick={() => onCheckboxChange(task.id, task.checked)}
                                            aria-label={translate('table.markComplete', { title: task.title || translate('common.untitled') })}
                                            aria-pressed={task.checked}
                                        >
                                            {task.checked && <Check size={10} strokeWidth={3} />}
                                        </button>
                                    </td>
                                    <td>
                                        <select
                                            value={task.stage}
                                            onChange={(e) => onStageChange(task.id, e.target.value)}
                                            className={`stage-select-tag ${getStageClass(task.stage)}`}
                                            aria-label={translate('table.stageOf', { title: task.title || translate('common.untitled') })}
                                        >
                                            {SELECTOR_STAGES.map(s => <option key={s} value={s}>{translate(`stages.${s}`)}</option>)}
                                        </select>
                                    </td>
                                    <td
                                        className="task-title-cell"
                                        title={task.title || translate('common.untitled')}
                                        onClick={(e) => onTitleClick(task, e)}
                                        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', paddingLeft: task.isSubtask ? `${(task.depth || 1) * 18 + 6}px` : '0' }}
                                    >
                                        {task.isSubtask && <span style={{ color: '#555', fontSize: '14px', marginRight: 'var(--space-1)' }}>↳</span>}
                                        {task.recurrent_interval > 0 && (
                                            <Repeat size={12} style={{ color: '#3b82f6', flexShrink: 0 }} title={translate('table.recurrentTooltip', { interval: task.recurrent_interval, unit: task.recurrent_unit })} />
                                        )}
                                        {task.title || translate('common.untitled')}
                                    </td>

                                    {/* Columna de Fecha Clickable con Popover Calendly */}
                                    <td className="date-cell-container" style={{ position: 'relative', zIndex: activeCalendlyPopoverTaskId === task.id ? 2000 : 1 }}>
                                        <span
                                            className={`date-cell-clickable rel-date-tag ${getRelativeDateDisplay(task.date, userTz).colorClass}`}
                                            onClick={(e) => {
                                                onOpenCalendly(task);
                                                e.stopPropagation();
                                            }}
                                        >
                                            {getRelativeDateDisplay(task.date, userTz).text}
                                        </span>

                                        {activeCalendlyPopoverTaskId === task.id && renderCalendlyPopover(task.id)}
                                    </td>

                                    {/* Columna de Tiempo Clickable vinculada al Popover de Duración */}
                                    <td className="time-cell-container" style={{ position: 'relative', zIndex: activeDurationPopoverTaskId === task.id ? 2000 : 1 }}>
                                        <span
                                            className="time-cell-clickable time-tag"
                                            onClick={(e) => {
                                                onOpenDuration(task.id);
                                                e.stopPropagation();
                                            }}
                                        >
                                            <Clock size={10} style={{ marginRight: 'var(--space-1)', display: 'inline-block', verticalAlign: 'middle' }} />
                                            <span style={{ verticalAlign: 'middle' }}>{formatTiempoDisplay(task.tiempo)}</span>
                                        </span>
                                        {activeDurationPopoverTaskId === task.id && renderDurationPopover(task)}
                                    </td>

                                    <td>
                                        <select
                                            value={task.priority}
                                            onChange={(e) => onPriorityChange(task.id, e.target.value)}
                                            aria-label={translate('table.priorityOf', { title: task.title || translate('common.untitled') })}
                                            className={`priority-select-tag ${task.priority}`}
                                        >
                                            <option value="baja">{translate('priority.baja')}</option>
                                            <option value="media">{translate('priority.media')}</option>
                                            <option value="alta">{translate('priority.alta')}</option>
                                        </select>
                                    </td>
                                    <td className="user-cell-td" style={{ zIndex: activeUserSelectTaskId === task.id && activeUserSelectField === 'assignee' ? 2500 : 1 }}>
                                        {renderUserCell(task, 'assignee')}
                                        {activeUserSelectTaskId === task.id && activeUserSelectField === 'assignee' && renderUserSelectPopover(task, 'assignee')}
                                    </td>
                                    <td className="user-cell-td" style={{ zIndex: activeUserSelectTaskId === task.id && activeUserSelectField === 'supervisor' ? 2500 : 1 }}>
                                        {renderUserCell(task, 'supervisor')}
                                        {activeUserSelectTaskId === task.id && activeUserSelectField === 'supervisor' && renderUserSelectPopover(task, 'supervisor')}
                                    </td>
                                    <td className="task-row-options-cell" style={{ position: 'relative', textAlign: 'center' }}>
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onToggleDropdown(task.id);
                                            }}
                                            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#888', padding: 'var(--space-1)' }}
                                            aria-label={translate('table.moreOptions')}
                                        >
                                            <MoreVertical size={16} />
                                        </button>
                                        {activeTaskDropdownId === task.id && (
                                            <div className="task-row-dropdown" onClick={(e) => e.stopPropagation()} style={{
                                                position: 'absolute',
                                                right: '0',
                                                top: '100%',
                                                background: '#232323',
                                                border: '1px solid #333',
                                                borderRadius: 'var(--radius-md)',
                                                padding: 'var(--space-2)',
                                                zIndex: 3000,
                                                width: '180px',
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.5)'
                                            }}>
                                                {deletingTaskId === task.id ? (
                                                    <div className="task-row-delete-confirm">
                                                        <span style={{ fontSize: '12px', color: '#ccc', marginBottom: 'var(--space-2)', display: 'block' }}>{translate('table.deleteConfirmTitle')}</span>
                                                        <div style={{ display: 'flex', gap: '4px' }}>
                                                            <button type="button" style={{ flex: 1, padding: 'var(--space-1)', background: '#333', border: 'none', borderRadius: 'var(--radius-sm)', color: '#fff', cursor: 'pointer' }} onClick={onCancelDelete}>{translate('actions.cancel')}</button>
                                                            <button type="button" style={{ flex: 1, padding: 'var(--space-1)', background: '#e11d48', border: 'none', borderRadius: 'var(--radius-sm)', color: '#fff', cursor: 'pointer' }} onClick={() => onDeleteTask(task.id)}>{translate('actions.delete')}</button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    getTaskPermissions(task.space_id)?.task_delete !== false && (
                                                        <button
                                                            type="button"
                                                            style={{ width: '100%', padding: 'var(--space-2)', background: 'transparent', border: 'none', color: '#e11d48', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}
                                                            onClick={() => onStartDelete(task.id)}
                                                        >
                                                            <Trash2 size={14} /> {translate('actions.delete')}
                                                        </button>
                                                    )
                                                )}
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Paginación — la vista de LISTA agrupa por etapa y no pagina (epic C2:
                `totalPages` llega como 1 en esa pestaña). */}
            {totalPages > 1 && (
            <div className="table-pagination">
                <button
                    onClick={() => onPageChange(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="page-nav-btn"
                >
                    {translate('table.pagination.previous')}
                </button>
                <span className="page-indicator">
                    {translate('table.pagination.indicator', { current: currentPage, total: totalPages })}
                </span>
                <button
                    onClick={() => onPageChange(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="page-nav-btn"
                >
                    {translate('table.pagination.next')}
                </button>
            </div>
            )}
        </div>
    );
};

export default TaskListView;
